import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth, usePermissions } from '../AuthContext';
import BrokerAreaPicker from './BrokerAreaPicker';

export type BrokerType = 'individual' | 'agency' | 'reference';
export type BrokerStatus = 'active' | 'inactive';

export type BrokerDoc = {
  id: string;
  brokerType: BrokerType;
  agencyName: string | null;
  brokerName: string;
  displayName: string;
  primaryContactId: string | null;
  phoneCountryCode: string; // "+91"
  phoneNational: string; // "9876543210" or "00" (no-phone token)
  phoneE164: string; // "+919876543210"
  email: string | null;
  city: string | null;
  area: string | null;
  address: string | null;
  reraNumber: string | null;
  dateOfBirth?: string | null;
  teamSize?: number | null;
  yearsExperience?: number | null;
  brokerInterestedProjectIds?: string[];
  brokerPotential?: 'unknown' | 'low' | 'medium' | 'high';
  agencyRoleAssessment?: 'unknown' | 'independent' | 'member' | 'head';
  notes: string | null;
  status: BrokerStatus;
  includeInBrokerAnalytics?: boolean;
  isAgencyHead?: boolean;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  approvalRequestedAt?: any;
  approvalRequestedBy?: string | null;
};

const COUNTRY_CODES = [
  { code: '+91', label: 'India (+91)' },
  { code: '+1', label: 'USA (+1)' },
  { code: '+971', label: 'UAE (+971)' },
  { code: '+44', label: 'UK (+44)' },
];

function normalizeDigits(value: string) {
  return value.replace(/\D/g, '');
}

function normalizeName(value: string) {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

function buildE164(countryCode: string, national: string) {
  const cc = countryCode.startsWith('+') ? countryCode : `+${countryCode}`;
  const nn = normalizeDigits(national);
  return `${cc}${nn}`;
}

export default function BrokerDrawer({
  open,
  onClose,
  onSaved,
  editing,
  elevateOverModal = false,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (saved?: { id: string; brokerName: string; agencyName: string | null }) => void;
  editing: BrokerDoc | null;
  elevateOverModal?: boolean;
}) {
  const { currentUser } = useAuth();
  const { canAction } = usePermissions();
  const canManage =
    canAction('brokers', 'addBroker') ||
    canAction('brokers', 'editBroker') ||
    canAction('leads', 'addBroker');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [brokerType, setBrokerType] = useState<BrokerType>('individual');
  const [agencyName, setAgencyName] = useState('');
  const [brokerName, setBrokerName] = useState('');
  const [status, setStatus] = useState<BrokerStatus>('active');

  const [phoneCountryCode, setPhoneCountryCode] = useState('+91');
  const [phoneNational, setPhoneNational] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [area, setArea] = useState('');
  const [address, setAddress] = useState('');
  const [reraNumber, setReraNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [notes, setNotes] = useState('');
  const [includeInBrokerAnalytics, setIncludeInBrokerAnalytics] = useState(true);
  const [isAgencyHead, setIsAgencyHead] = useState(false);
  const [drawerTopOffset, setDrawerTopOffset] = useState(0);

  const [agencyOptions, setAgencyOptions] = useState<string[]>([]);
  const [agencyBrokers, setAgencyBrokers] = useState<Array<{ id: string; brokerName: string; agencyName: string | null; isAgencyHead?: boolean }>>([]);

  const isEdit = !!editing?.id;
  const isReferenceType = brokerType === 'reference';

  useEffect(() => {
    if (!open) {
      setDrawerTopOffset(0);
      return;
    }
    let raf = 0;
    const compute = () => {
      const el = document.querySelector<HTMLElement>('[data-role="app-header"]');
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      const visible = Math.max(0, Math.min(rect.height, rect.bottom));
      return Math.round(visible);
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = compute();
        setDrawerTopOffset((prev) => (prev === next ? prev : next));
      });
    };
    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [open]);

  const displayName = useMemo(() => {
    const a = agencyName.trim();
    const b = brokerName.trim();
    return brokerType === 'agency' && a ? a : b;
  }, [agencyName, brokerName, brokerType]);

  const agencyCanonByNorm = useMemo(() => {
    const map = new Map<string, string>();
    agencyOptions.forEach((name) => map.set(normalizeName(name), name));
    return map;
  }, [agencyOptions]);

  const agencyMatch = useMemo(() => {
    if (brokerType !== 'agency') return null;
    const norm = normalizeName(agencyName);
    if (!norm) return null;
    return agencyCanonByNorm.get(norm) || null;
  }, [agencyCanonByNorm, agencyName, brokerType]);
  const selectedAgencyCanonical = useMemo(
    () => brokerType === 'agency' ? (agencyMatch || agencyName.trim().replace(/\s+/g, ' ')) : '',
    [agencyMatch, agencyName, brokerType]
  );
  const currentAgencyHead = useMemo(() => {
    const agencyNorm = normalizeName(selectedAgencyCanonical);
    if (!agencyNorm) return null;
    return agencyBrokers.find((broker) =>
      broker.isAgencyHead &&
      normalizeName(broker.agencyName || '') === agencyNorm &&
      (!editing?.id || broker.id !== editing.id)
    ) || null;
  }, [agencyBrokers, editing?.id, selectedAgencyCanonical]);

  useEffect(() => {
    if (!open) return;
    // Load existing agency names for autocomplete + canonicalization.
    const qAgencies = query(collection(db, 'brokers'), where('brokerType', '==', 'agency'));
    const unsub = onSnapshot(
      qAgencies,
      (snap) => {
        const names = snap.docs
          .map((d) => (d.data() as any)?.agencyName)
          .filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
        // unique by normalized form
        const uniq = new Map<string, string>();
        for (const n of names) {
          const norm = normalizeName(n);
          if (!uniq.has(norm)) uniq.set(norm, n.trim().replace(/\s+/g, ' '));
        }
        setAgencyOptions(Array.from(uniq.values()).sort((a, b) => a.localeCompare(b)));
        setAgencyBrokers(
          snap.docs.map((d) => {
            const data = d.data() as any;
            return {
              id: d.id,
              brokerName: String(data?.brokerName || '').trim(),
              agencyName: typeof data?.agencyName === 'string' ? data.agencyName : null,
              isAgencyHead: data?.isAgencyHead === true,
            };
          })
        );
      },
      () => {
        // ignore; autocomplete is optional
        setAgencyOptions([]);
        setAgencyBrokers([]);
      }
    );
    return () => unsub();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (editing) {
      setBrokerType(editing.brokerType);
      setAgencyName(editing.agencyName || '');
      setBrokerName(editing.brokerName || '');
      setStatus(editing.status || 'active');
      setPhoneCountryCode(editing.phoneCountryCode || '+91');
      setPhoneNational(editing.phoneNational || '');
      setEmail(editing.email || '');
      setCity(editing.city || '');
      setArea(editing.area || '');
      setAddress(editing.address || '');
      setReraNumber(editing.reraNumber || '');
      setDateOfBirth(String((editing as any).dateOfBirth || (editing as any).dob || ''));
      setNotes(editing.notes || '');
      setIncludeInBrokerAnalytics(
        typeof (editing as any).includeInBrokerAnalytics === 'boolean'
          ? Boolean((editing as any).includeInBrokerAnalytics)
          : true
      );
      setIsAgencyHead(Boolean((editing as any).isAgencyHead));
    } else {
      setBrokerType('individual');
      setAgencyName('');
      setBrokerName('');
      setStatus('active');
      setPhoneCountryCode('+91');
      setPhoneNational('');
      setEmail('');
      setCity('');
      setArea('');
      setAddress('');
      setReraNumber('');
      setDateOfBirth('');
      setNotes('');
      setIncludeInBrokerAnalytics(true);
      setIsAgencyHead(false);
    }
  }, [open, editing]);

  useEffect(() => {
    if (brokerType === 'reference') setIncludeInBrokerAnalytics(false);
    if (brokerType !== 'agency') setIsAgencyHead(false);
  }, [brokerType]);

  const validate = () => {
    const bn = brokerName.trim();
    const an = agencyName.trim();
    const pn = normalizeDigits(phoneNational);

    if (!bn) return 'Broker Name is required.';
    if (brokerType === 'agency' && !an) return 'Agency Name is required for agency brokers.';
    if (!pn) return 'Phone number is required.';
    if (pn !== '00' && pn.length !== 10) {
      return 'Phone must be exactly 10 digits, or use 00 when number is unavailable.';
    }
    return null;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManage) return;
    setError(null);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    const phoneDigits = normalizeDigits(phoneNational);
    const isNoPhoneToken = phoneDigits === '00';
    const phoneE164 = buildE164(phoneCountryCode, phoneNational);
    const phoneKey = phoneE164; // doc id; "+" is valid in Firestore ids

    setSaving(true);
    let stage = 'starting';
    try {
      const phoneRef = doc(db, 'brokerPhoneNumbers', phoneKey);
      if (!isNoPhoneToken) {
        // Enforce unique contact number across entire broker list (brokers + contacts).
        stage = 'checking duplicate phone in brokers';
        const qDupBroker = query(collection(db, 'brokers'), where('phoneE164', '==', phoneE164), limit(1));
        const dupBrokerSnap = await getDocs(qDupBroker);
        if (!dupBrokerSnap.empty) {
          const dupId = dupBrokerSnap.docs[0].id;
          if (!isEdit || dupId !== editing?.id) {
            setError('This contact number is already used by another broker.');
            return;
          }
        }

        stage = 'checking duplicate phone registry';
        // Avoid collectionGroup queries (can be blocked by rules / indexes).
        // We keep a helper doc per phone so uniqueness checks are deterministic.
        const phoneSnap = await getDoc(phoneRef);
        if (phoneSnap.exists()) {
          const data = phoneSnap.data() as { brokerId?: string | null };
          const mappedBrokerId = data?.brokerId || null;
          if (!isEdit || mappedBrokerId !== editing?.id) {
            setError('This contact number is already used by another broker contact.');
            return;
          }
        }
      }

      // Canonicalize agency name to avoid duplicates by spaces/case/spelling variants.
      const agencyCanonical =
        brokerType === 'agency'
          ? (agencyMatch || agencyName.trim().replace(/\s+/g, ' '))
          : null;

      const payload = {
        brokerType,
        agencyName: agencyCanonical,
        agencyNameLower: agencyCanonical ? normalizeName(agencyCanonical) : null,
        brokerName: brokerName.trim(),
        displayName: displayName.trim(),
        displayNameLower: displayName.trim().toLowerCase(),
        status,
        phoneCountryCode,
        phoneNational: normalizeDigits(phoneNational),
        phoneE164,
        email: email.trim() || null,
        city: city.trim() || null,
        area: area.trim() || null,
        address: address.trim() || null,
        reraNumber: reraNumber.trim() || null,
        dateOfBirth: dateOfBirth || null,
        dob: dateOfBirth || null,
        notes: notes.trim() || null,
        includeInBrokerAnalytics: brokerType === 'reference' ? false : includeInBrokerAnalytics,
        isAgencyHead: brokerType === 'agency' ? isAgencyHead : false,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || null,
      };

      let savedBroker: { id: string; brokerName: string; agencyName: string | null } | undefined;

      if (isEdit && editing) {
        stage = 'updating broker (transaction)';
        const brokerRef = doc(db, 'brokers', editing.id);
        const primaryContactId = editing.primaryContactId;
        const contactRef = primaryContactId ? doc(db, 'brokers', editing.id, 'contacts', primaryContactId) : null;
        const oldPhoneKey = editing.phoneE164;
        const oldIsNoPhoneToken = normalizeDigits(editing.phoneNational || '') === '00';
        const oldPhoneRef = oldPhoneKey ? doc(db, 'brokerPhoneNumbers', oldPhoneKey) : null;

        await runTransaction(db, async (tx) => {
          let shouldDeleteOldPhoneRef = false;

          // Re-check inside txn to prevent races.
          if (!isNoPhoneToken) {
            const currentPhoneSnap = await tx.get(phoneRef);
            if (currentPhoneSnap.exists()) {
              const data = currentPhoneSnap.data() as { brokerId?: string | null };
              const mappedBrokerId = data?.brokerId || null;
              if (mappedBrokerId !== editing.id) {
                throw new Error('This contact number is already used by another broker contact.');
              }
            }
          }

          if (oldPhoneRef && (!oldIsNoPhoneToken || oldPhoneKey !== phoneKey)) {
            const oldSnap = await tx.get(oldPhoneRef);
            const oldData = oldSnap.exists() ? (oldSnap.data() as { brokerId?: string | null }) : null;
            shouldDeleteOldPhoneRef = oldSnap.exists() && (oldData?.brokerId || null) === editing.id;
          }

          tx.update(brokerRef, payload);

          if (contactRef) {
            // Keep the primary contact in sync (v1: primary contact == brokerName + phone/email).
            tx.update(contactRef, {
              contactName: brokerName.trim(),
              phoneCountryCode,
              phoneNational: normalizeDigits(phoneNational),
              phoneE164,
              email: email.trim() || null,
              roleTitle: brokerType === 'agency' ? 'Owner' : brokerType === 'reference' ? 'Reference' : 'Broker',
              status,
              updatedAt: serverTimestamp(),
              updatedBy: currentUser?.email || null,
            });
          }

          // Maintain the helper mapping (create if missing). If phone changed, delete old mapping.
          if (!isNoPhoneToken) {
            tx.set(
              phoneRef,
              {
                phoneE164,
                brokerId: editing.id,
                contactId: primaryContactId || null,
                updatedAt: serverTimestamp(),
                updatedBy: currentUser?.email || null,
                createdAt: serverTimestamp(),
                createdBy: currentUser?.email || null,
              },
              { merge: true }
            );
          }

          if (oldPhoneRef && shouldDeleteOldPhoneRef) {
            tx.delete(oldPhoneRef);
          }
        });
        savedBroker = {
          id: editing.id,
          brokerName: brokerName.trim(),
          agencyName: brokerType === 'agency' ? (agencyCanonical || null) : null,
        };
      } else {
        stage = 'creating broker (transaction)';
        const brokerRef = doc(collection(db, 'brokers'));
        const contactRef = doc(collection(db, 'brokers', brokerRef.id, 'contacts'));

        await runTransaction(db, async (tx) => {
          if (!isNoPhoneToken) {
            const currentPhoneSnap = await tx.get(phoneRef);
            if (currentPhoneSnap.exists()) {
              throw new Error('This contact number is already used by another broker contact.');
            }
          }

          tx.set(brokerRef, {
            ...payload,
            primaryContactId: contactRef.id,
            createdAt: serverTimestamp(),
            createdBy: currentUser?.email || null,
          });

          tx.set(contactRef, {
            contactName: brokerName.trim(),
            phoneCountryCode,
            phoneNational: normalizeDigits(phoneNational),
            phoneE164,
            email: email.trim() || null,
            roleTitle: brokerType === 'agency' ? 'Owner' : brokerType === 'reference' ? 'Reference' : 'Broker',
            status,
            createdAt: serverTimestamp(),
            createdBy: currentUser?.email || null,
            updatedAt: serverTimestamp(),
            updatedBy: currentUser?.email || null,
          });

          if (!isNoPhoneToken) {
            tx.set(phoneRef, {
              phoneE164,
              brokerId: brokerRef.id,
              contactId: contactRef.id,
              createdAt: serverTimestamp(),
              createdBy: currentUser?.email || null,
              updatedAt: serverTimestamp(),
              updatedBy: currentUser?.email || null,
            });
          }
        });
        savedBroker = {
          id: brokerRef.id,
          brokerName: brokerName.trim(),
          agencyName: brokerType === 'agency' ? (agencyCanonical || null) : null,
        };
      }

      if (brokerType === 'agency' && isAgencyHead && agencyCanonical && savedBroker?.id) {
        stage = 'assigning agency head';
        const agencyNorm = normalizeName(agencyCanonical);
        const qAgencyBrokers = query(
          collection(db, 'brokers'),
          where('brokerType', '==', 'agency'),
          where('agencyNameLower', '==', agencyNorm)
        );
        const agencySnap = await getDocs(qAgencyBrokers);
        const batch = writeBatch(db);
        agencySnap.docs.forEach((docSnap) => {
          if (docSnap.id !== savedBroker?.id) {
            batch.update(docSnap.ref, {
              isAgencyHead: false,
              updatedAt: serverTimestamp(),
              updatedBy: currentUser?.email || null,
            });
          }
        });
        batch.update(doc(db, 'brokers', savedBroker.id), {
          isAgencyHead: true,
          agencyHeadAssignedAt: serverTimestamp(),
          agencyHeadAssignedBy: currentUser?.email || null,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.email || null,
        });
        await batch.commit();
      }

      onSaved(savedBroker);
      onClose();
    } catch (err: any) {
      const msg = err?.message || 'Failed to save broker.';
      // eslint-disable-next-line no-console
      console.error('Broker save failed', err);
      setError(`${msg} (stage: ${typeof stage === 'string' ? stage : 'unknown'})`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={
        elevateOverModal
          ? {
              zIndex: (theme) => theme.zIndex.modal + 2,
              '& .MuiDrawer-paper': {
                zIndex: (theme) => theme.zIndex.modal + 2,
                top: `${drawerTopOffset}px`,
                height: `calc(100% - ${drawerTopOffset}px)`,
              },
            }
          : {
              '& .MuiDrawer-paper': {
                top: `${drawerTopOffset}px`,
                height: `calc(100% - ${drawerTopOffset}px)`,
              },
            }
      }
    >
      <Box sx={{ width: 420, p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">{isEdit ? 'Edit Broker' : 'Add Broker'}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        <Box component="form" onSubmit={handleSave} noValidate>
          <Typography variant="subtitle2" sx={{ mt: 1, color: 'text.secondary' }}>
            Type
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={brokerType}
            onChange={(_, v) => {
              if (!v) return;
              setBrokerType(v);
            }}
            sx={{
              mt: 1,
              mb: 1,
              width: '100%',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 1,
              '& .MuiToggleButton-root': {
                borderRadius: 2,
                textTransform: 'none',
              },
            }}
          >
            <ToggleButton value="individual">Individual</ToggleButton>
            <ToggleButton value="agency">Agency</ToggleButton>
            <ToggleButton value="reference">Reference</ToggleButton>
          </ToggleButtonGroup>

          {brokerType === 'agency' && (
            <>
              <Autocomplete
                freeSolo
                options={agencyOptions}
                value={agencyName}
                onInputChange={(_, v) => setAgencyName(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    required
                    fullWidth
                    label="Agency Name"
                    helperText={
                      agencyMatch && normalizeName(agencyMatch) === normalizeName(agencyName)
                        ? `Using existing agency: ${agencyMatch}`
                        : undefined
                    }
                  />
                )}
              />
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={
                  <Switch
                    checked={isAgencyHead}
                    onChange={(e) => setIsAgencyHead(e.target.checked)}
                  />
                }
                label="Head of this agency"
              />
              {isAgencyHead && currentAgencyHead ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Saving will replace current agency head: {currentAgencyHead.brokerName || 'Unnamed broker'}.
                </Alert>
              ) : null}
            </>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            label={isReferenceType ? 'Reference Name' : 'Broker Name'}
            value={brokerName}
            onChange={(e) => setBrokerName(e.target.value)}
          />

          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <FormControl sx={{ minWidth: 140 }}>
              <InputLabel id="cc-label">Code</InputLabel>
              <Select
                labelId="cc-label"
                label="Code"
                value={phoneCountryCode}
                onChange={(e) => setPhoneCountryCode(e.target.value)}
              >
                {COUNTRY_CODES.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Phone (10 digits or 00)"
              value={phoneNational}
              onChange={(e) => setPhoneNational(normalizeDigits(e.target.value).slice(0, 10))}
              inputProps={{ inputMode: 'numeric' }}
            />
          </Stack>

          <TextField
            margin="normal"
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <TextField
            margin="normal"
            fullWidth
            label="Date of Birth"
            type="date"
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Optional. Used for birthday wishes."
          />

          <Stack direction="row" spacing={1}>
            <TextField margin="normal" fullWidth label="City" value={city} onChange={(e) => setCity(e.target.value)} />
            <BrokerAreaPicker value={area} onChange={setArea} label="Areas you work in" />
          </Stack>

          <TextField
            margin="normal"
            fullWidth
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            multiline
            minRows={2}
          />

          <Stack direction="row" spacing={1}>
            <TextField
              margin="normal"
              fullWidth
              label="RERA Number"
              value={reraNumber}
              onChange={(e) => setReraNumber(e.target.value)}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="broker-status-label">Status</InputLabel>
              <Select
                labelId="broker-status-label"
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value as BrokerStatus)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <TextField
            margin="normal"
            fullWidth
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
          />

          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={isReferenceType ? false : includeInBrokerAnalytics}
                disabled={isReferenceType}
                onChange={(e) => setIncludeInBrokerAnalytics(e.target.checked)}
              />
            }
            label="Include in broker analytics"
          />
          {isReferenceType && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              Reference type is excluded from broker analytics.
            </Typography>
          )}

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3 }}
            disabled={!canManage || saving}
          >
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Broker'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
